import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDateTime } from '../../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { toast } from 'sonner';
import {
  User, Calendar, History, UserCog, Trash2, UserPlus, Clock, Activity, Pill,
  FlaskConical, Stethoscope, StickyNote, Share2, FileText, Scan, DoorOpen,
} from 'lucide-react';

const EVENT_ICONS = {
  registration: UserPlus, admission: Activity, appointment: Calendar, prescription: Pill,
  lab_test: FlaskConical, lab_test_completed: FlaskConical, imaging_test: Scan,
  imaging_test_completed: Scan, procedure: Stethoscope, procedure_completed: Stethoscope,
  reassignment: UserCog, deletion: Trash2, note: StickyNote, referral: Share2,
  document_upload: FileText, discharge: DoorOpen,
};

const EVENT_COLORS = {
  registration: 'bg-green-100 text-green-800', admission: 'bg-blue-100 text-blue-800',
  prescription: 'bg-red-100 text-red-800', lab_test: 'bg-cyan-100 text-cyan-800',
  lab_test_completed: 'bg-emerald-100 text-emerald-800', imaging_test: 'bg-blue-100 text-blue-800',
  imaging_test_completed: 'bg-indigo-100 text-indigo-800', procedure: 'bg-violet-100 text-violet-800',
  procedure_completed: 'bg-purple-100 text-purple-800', reassignment: 'bg-amber-100 text-amber-800',
  deletion: 'bg-red-100 text-red-800', note: 'bg-purple-100 text-purple-800',
  referral: 'bg-teal-100 text-teal-800', document_upload: 'bg-green-100 text-green-800',
  discharge: 'bg-slate-100 text-slate-800',
};

const eventColor = (type) => EVENT_COLORS[type] || 'bg-slate-100 text-slate-800';

const EventIcon = ({ type }) => {
  const Icon = EVENT_ICONS[type] || Clock;
  return <Icon className="w-4 h-4" />;
};

const ACTION_LINKS = [
  { match: (e) => e.event_type === 'prescription' && e.metadata?.prescription_id, to: '/prescriptions', icon: FileText, label: 'View Prescription' },
  { match: (e) => ['lab_test', 'lab_test_completed'].includes(e.event_type) && e.metadata?.test_id, to: '/lab-tests', icon: FlaskConical, label: 'View Test Details' },
  { match: (e) => e.event_type === 'imaging_test' && e.metadata?.test_id, to: '/imaging-tests', icon: Scan, label: 'View Imaging' },
  { match: (e) => e.event_type === 'procedure' && e.metadata?.procedure_id, to: '/procedures', icon: Stethoscope, label: 'View Procedure' },
];

const TimelineEntry = ({ entry }) => {
  const navigate = useNavigate();
  return (
    <div className="relative pl-8 pb-6 border-l-2 border-slate-200 last:border-0" data-testid={`timeline-entry-${entry.id}`}>
      <div className={`absolute -left-3 top-0 w-6 h-6 rounded-full flex items-center justify-center ${eventColor(entry.event_type)}`}>
        <EventIcon type={entry.event_type} />
      </div>
      <div className="bg-white p-3 rounded-lg border border-slate-100 hover:border-red-200 transition-colors">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <h4 className="font-semibold text-sm text-slate-900">{entry.event_title}</h4>
            <p className="text-xs text-slate-400 mt-0.5">{formatDateTime(entry.created_at)}</p>
          </div>
          <Badge className={eventColor(entry.event_type)}>{entry.event_type?.replace('_', ' ')}</Badge>
        </div>

        <p className="text-sm text-slate-700 mb-2">{entry.event_description}</p>

        <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
          <p className="text-xs text-slate-500">
            <User className="w-3 h-3 inline mr-1" />
            {entry.performed_by_name} · {entry.performed_by_role?.replace('_', ' ')}
          </p>
          <div className="flex gap-1">
            {ACTION_LINKS.filter(a => a.match(entry)).map(({ to, icon: Icon, label }) => (
              <Button key={to} size="sm" variant="outline" className="text-xs h-7" onClick={() => navigate(to)}>
                <Icon className="w-3 h-3 mr-1" />
                {label}
              </Button>
            ))}
            {entry.event_type === 'document_upload' && entry.metadata?.document_id && (
              <Button size="sm" variant="outline" className="text-xs h-7 bg-green-50" onClick={() => toast.info('Document viewer coming soon')}>
                <FileText className="w-3 h-3 mr-1" />
                View Document
              </Button>
            )}
            {entry.metadata?.has_report && (
              <Badge className="bg-green-100 text-green-800 text-xs"><FileText className="w-3 h-3 mr-1" />Report Available</Badge>
            )}
          </div>
        </div>

        {entry.metadata && (entry.metadata.medicine_count || entry.metadata.test_name || entry.metadata.filename) && (
          <div className="mt-2 p-2 bg-slate-50 rounded text-xs space-y-0.5">
            {entry.metadata.medicine_count && <p className="text-slate-600">{entry.metadata.medicine_count} medicine(s)</p>}
            {entry.metadata.test_name && <p className="text-slate-600">{entry.metadata.test_name}</p>}
            {entry.metadata.filename && <p className="text-slate-600">{entry.metadata.filename}</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export const PatientTimelineTab = ({ timeline }) => (
  <Card data-testid="patient-timeline-tab">
    <CardHeader>
      <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
        <History className="w-5 h-5 text-red-500" />
        Complete Patient Journey
      </CardTitle>
    </CardHeader>
    <CardContent>
      {timeline.length === 0 ? (
        <p className="text-center text-slate-500 py-8">No timeline entries yet</p>
      ) : (
        <div className="space-y-4">
          {timeline.map((entry) => <TimelineEntry key={entry.id} entry={entry} />)}
        </div>
      )}
    </CardContent>
  </Card>
);
